import React from 'react';
import { Activity, Database, Cpu, HardDrive, Clock, Wifi } from 'lucide-react';
import { useSystemHealth } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner, StatCard } from '@/components/UI';
import { formatNumber, statusColor } from '@/utils';
import type { SystemHealth as SystemHealthType } from '@/types';

function ProgressBar({ value, max = 100, label }: { value: number; max?: number; label: string }) {
  const pct = Math.min((value / max) * 100, 100);
  const color = pct > 80 ? 'bg-loss' : pct > 60 ? 'bg-warning' : 'bg-profit';

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-surface-400">{label}</span>
        <span className="text-xs font-medium text-surface-300">{value.toFixed(1)}%</span>
      </div>
      <div className="h-2 bg-surface-800 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function SystemHealthPage() {
  const { data: health, isLoading } = useSystemHealth();

  if (isLoading) return <LoadingSpinner text="Checking system health..." />;

  const h = health as SystemHealthType | null;

  return (
    <div className="space-y-6 animate-in">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold text-surface-100">System Health</h1>
        <Badge
          variant={
            h?.status === 'healthy' ? 'profit' :
            h?.status === 'degraded' ? 'warning' : 'loss'
          }
        >
          {h?.status?.toUpperCase() ?? 'Unknown'}
        </Badge>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Database"
          value={h?.database ?? 'Unknown'}
          icon={<Database className="w-5 h-5" />}
        />
        <StatCard
          label="Redis"
          value={h?.redis ?? 'Unknown'}
          icon={<Wifi className="w-5 h-5" />}
        />
        <StatCard
          label="Uptime"
          value={formatUptime(h?.uptime_seconds ?? 0)}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          label="Version"
          value={h?.version ?? '0.1.0'}
          icon={<Activity className="w-5 h-5" />}
        />
      </div>

      {/* Resource Usage */}
      <Card title="Resource Usage">
        <div className="space-y-4">
          <ProgressBar value={h?.cpu_percent ?? 0} label="CPU Usage" />
          <ProgressBar value={h?.memory_percent ?? 0} label="Memory Usage" />
          <ProgressBar value={h?.disk_percent ?? 0} label="Disk Usage" />
        </div>
      </Card>

      {/* Trading Status */}
      <Card title="Trading Status">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-surface-500">Active Strategies</p>
            <p className="text-2xl font-bold text-surface-100">{h?.active_strategies ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-surface-500">Open Positions</p>
            <p className="text-2xl font-bold text-surface-100">{h?.open_positions ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-surface-500">Pending Orders</p>
            <p className="text-2xl font-bold text-surface-100">{h?.pending_orders ?? 0}</p>
          </div>
          <div>
            <p className="text-xs text-surface-500">Last Trade</p>
            <p className="text-sm font-medium text-surface-300">
              {h?.last_trade_at ? new Date(h.last_trade_at).toLocaleString() : 'None'}
            </p>
          </div>
        </div>
      </Card>

      {/* Service Connections */}
      <Card title="Service Connections">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { name: 'PostgreSQL', status: h?.database ?? 'unknown' },
            { name: 'Redis', status: h?.redis ?? 'unknown' },
          ].map((service) => (
            <div key={service.name} className="flex items-center justify-between p-3 rounded-lg bg-surface-800/50">
              <div className="flex items-center gap-2">
                <div className={`w-2.5 h-2.5 rounded-full ${
                  service.status === 'connected' ? 'bg-profit' : 'bg-loss'
                }`} />
                <span className="text-sm text-surface-200">{service.name}</span>
              </div>
              <span className={`text-xs font-medium ${statusColor(service.status)}`}>
                {service.status.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}
